import { useCallback, useEffect, useMemo, useState } from 'react';
import { pmGet, pmPost, type PmProject } from '../api';
import './ProjectVisibility.css';
/* The panel and its copy live with Inventory Registry, which is the module these two
   screens are reached from. One definition, as with the screens themselves. */
import { HelpPanel } from '../../portfolio_definition/HelpPanel';
import { PROJECT_VISIBILITY } from '../../portfolio_definition/helpContent';
import { SelectField, PROMPT } from '@platform/ui-kit';

/**
 * Settings → Project Execution → Project Visibility.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/setup-project-execution-visibility.ejs`.
 * Same markup, same class names, same copy, same behaviour — the source's own IIFE is
 * reproduced as component state, statement for statement.
 *
 * WHAT IT DOES, in the source's words: control who can access each delivery project. Anyone
 * granted here can open that project's execution screens; everyone else does not see it in
 * their project pickers at all.
 *
 * TWO SOURCES OF PEOPLE, and the distinction is the screen's whole point. Team members come
 * from the project's streams; login users are added by hand from the platform's account
 * list. The `Source` column says which, and `renderAddUsers` only offers accounts that are
 * not already on the roster.
 *
 * NEWLY GRANTED PEOPLE START AS VIEW-ONLY — the server's `/visibility/save` writes preset
 * 'ViewOnly', and disabling DELETES the row rather than writing preset 'None'. A missing row
 * means "never added" and hides the project; 'None' would leave it visible but empty. That
 * asymmetry is the source's and is preserved on the server side; this screen only sends the
 * flag.
 *
 * TOASTS BECOME AN INLINE STATUS LINE. The source calls `M.toast(...)` — Materialize's
 * global toast — for every outcome. Materialize is not in this SPA, so each toast becomes a
 * line under the action that produced it, carrying the SAME text and the same ✅ / ❌
 * markers. Nothing is reworded.
 */

interface RosterPerson {
  name: string | null;
  email: string;
  role: string | null;
  stream: string | null;
  source: string;
  has_login?: boolean;
  access?: unknown | null;
  /** The role this person holds ON THIS PROJECT. */
  member_role?: string | null;
}

interface RosterUser {
  user_id: number;
  full_name: string;
  email: string;
  role: string;
}

interface RosterResponse {
  success: boolean;
  error?: string;
  project: PmProject;
  people: RosterPerson[];
  users: RosterUser[];
  /** Roles this viewer may grant, decided server-side. */
  grantableRoles?: string[];
  roleLabels?: Record<string, string>;
  viewerRole?: string | null;
}

/** A roster row as the screen holds it — the source's `people[]` entry. */
interface Person {
  name: string;
  email: string;
  role: string;
  stream: string;
  source: string;
  enabled: boolean;
  /** The role this person holds ON THIS PROJECT — null until one is chosen. */
  member_role: string | null;
}

type Toast = { kind: 'ok' | 'err' | 'warn'; text: string } | null;

export function ProjectVisibility() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [current, setCurrent] = useState<PmProject | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [users, setUsers] = useState<RosterUser[]>([]);
  const [addEmail, setAddEmail] = useState('');
  /* The add-user control is revealed by the header action, not always open. */
  const [adding, setAdding] = useState(false);
  const [addRole, setAddRole] = useState('');
  /* Which project roles THIS viewer may hand out, and their labels — both from the server,
     which decides the rule and enforces it again on save. */
  const [grantable, setGrantable] = useState<string[]>([]);
  const [roleLabels, setRoleLabels] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  /* `loadProjects()` — the option label is id, then description, then methodology in
     brackets, in that order, exactly as the source builds it. */
  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ success: boolean; error?: string; projects: PmProject[] }>(
          '/settings/project-execution/projects',
        );
        if (!d.success) {
          setToast({ kind: 'err', text: `❌ ${d.error || 'Failed to load projects'}` });
          return;
        }
        setProjects(d.projects ?? []);
      } catch (e) {
        setToast({ kind: 'err', text: `❌ ${(e as Error).message}` });
      }
    })();
  }, []);

  const loadRoster = useCallback(async (id: string) => {
    try {
      const d = await pmGet<RosterResponse>(
        `/settings/project-execution/roster?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setToast({ kind: 'err', text: `❌ ${d.error || 'Failed'}` });
        return;
      }
      setCurrent(d.project);
      setUsers(d.users ?? []);
      setGrantable(d.grantableRoles ?? []);
      setRoleLabels(d.roleLabels ?? {});
      setPeople(
        (d.people ?? []).map((p) => ({
          name: p.name ?? '',
          email: p.email,
          role: p.role ?? '',
          stream: p.stream ?? '',
          member_role: p.member_role ?? null,
          source: p.source,
          // Any access row at all means they currently have visibility.
          enabled: !!p.access,
        })),
      );
      setAddEmail('');
      setToast(null);
    } catch (e) {
      setToast({ kind: 'err', text: `❌ ${(e as Error).message}` });
    }
  }, []);

  /** `byEmail` in the source — used to keep the add-user list free of duplicates. */
  const onRoster = useMemo(
    () => new Set(people.map((p) => p.email.toLowerCase())),
    [people],
  );

  /*
   * ADD BY EMAIL. The email is the identity: if it matches a login user the row links to
   * that account, and if it does not, the grant still stands and links itself the day the
   * account is created. Requiring an existing account first would make it impossible to
   * invite anyone new, which was the previous control's real limitation.
   */
  const addUser = () => {
    const email = addEmail.trim().toLowerCase();
    if (!email) {
      setToast({ kind: 'err', text: '❌ Enter an email address' });
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setToast({ kind: 'err', text: '❌ That does not look like an email address' });
      return;
    }
    if (onRoster.has(email)) {
      setToast({ kind: 'err', text: '❌ That person is already on the roster' });
      return;
    }
    const u = users.find((x) => (x.email || '').toLowerCase() === email);
    setPeople((cur) => [
      ...cur,
      {
        name: u?.full_name ?? '',
        email,
        role: '',
        stream: '',
        source: u ? 'Login user' : 'Invited',
        member_role: addRole || null,
        enabled: true,
      },
    ]);
    setAddEmail('');
    setAddRole('');
    setToast({ kind: 'ok', text: `✅ ${email} added — Save Visibility to apply` });
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/settings/project-execution/visibility/save',
        {
          delivery_project_id: current.delivery_project_id,
          /* `member_role` rides along with each entry. The server re-checks that this
             viewer may grant it — the picker only narrows the choice, it does not enforce. */
          entries: people.map((p) => ({
            email: p.email,
            name: p.name,
            enabled: p.enabled,
            member_role: p.member_role,
          })),
        },
      );
      setToast(
        d.success
          ? { kind: 'ok', text: '✅ Visibility saved' }
          : { kind: 'err', text: `❌ ${d.error || 'Save failed'}` },
      );
      /* Re-read after a save so roles the server rejected or normalised are shown as
         stored, not as typed. */
      if (d.success) void loadRoster(current.delivery_project_id);
    } catch (e) {
      setToast({ kind: 'err', text: `❌ ${(e as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const addable = users.filter((u) => !onRoster.has((u.email || '').toLowerCase()));

  return (
    <div className="pm-pvis">
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Project Visibility</h1>
            <p className="page-header__subtitle">
              Control which team members and users can access each delivery project
            </p>
          </div>
          <div className="pd-page-header__actions">
            <HelpPanel help={PROJECT_VISIBILITY} />
            {/* The primary action sits in the SCREEN header, not inside the roster card —
                it is what you came here to do, and it should not move as the table grows. */}
            <button
              type="button"
              className="pvis-add"
              onClick={() => setAdding(true)}
              disabled={!current}
              title={current ? undefined : 'Choose a Project first'}
            >
              + Add user
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-content">
          <div className="row" style={{ marginBottom: 0 }}>
            <div className="col m6">
              <SelectField
                id="pvis-project"
                label="Project"
                orientation="stacked"
                width="lg"
                placeholder={PROMPT.project}
                value={projectId}
                onChange={(v) => {
                  setProjectId(v);
                  /* Clearing the field clears the roster: leaving the previous project's
                     people on screen under "no project" would be worse than an empty one. */
                  if (v) void loadRoster(v);
                  else {
                    setCurrent(null);
                    setPeople([]);
                  }
                }}
                /* The description is no longer truncated. It used to be capped at 52
                   characters because a native <select> sizes itself to its WIDEST option, so
                   a few hundred characters of description dragged the control and its list
                   off the screen. SelectField wraps instead, so the whole name is readable
                   and nothing overflows. */
                options={projects.map((p) => ({
                  value: p.delivery_project_id,
                  id: p.delivery_project_id,
                  description: `${p.description ?? ''}${p.methodology ? ` (${p.methodology})` : ''}`.trim(),
                }))}
              />
            </div>
            {/*
              The project id and methodology used to be printed here, in a second column
              beside the field — floating text with no boundary of its own, which read as a
              stray label rather than a value. The pulldown already shows both, so it said
              nothing the field did not.
            */}
          </div>
        </div>
      </div>

      {current ? (
        <div className="card">
          <div className="card-content">
            {/*
              The heading prose is gone. It explained the Source column and the add-user
              control, both of which now say what they are where they are — the column is
              labelled and the action sits in this header rather than at the foot of the
              table. Copy that repeats what the UI already shows is copy nobody reads.
            */}
            {/* No "People with access" heading — the columns say what this is, and the
                action that used to live here has moved to the screen header. */}
            <div className="pvis-roster-hd">
              <span className="pvis-roster-count">
                {people.filter((x) => x.enabled).length} of {people.length} with access
              </span>
            </div>

            {/* Revealed by the header action rather than sitting open at the foot of the
                table, where it read as part of the roster. */}
            {/*
              ADD BY EMAIL, THEN CHOOSE THE ROLE FOR THIS PROJECT.

              It used to be a dropdown of existing login users, which cannot invite anyone
              who does not already have an account — the common case. An email is the
              identity here: if it matches a login user the row links to them, and if it does
              not, the grant still stands and links itself the day that account is created.

              The role list is what the SERVER said this viewer may grant. It is not the
              authority — the save re-derives it — but offering roles you cannot grant would
              be a dead control.
            */}
            {adding ? (
              <div className="pvis-addrow">
                <div className="pvis-fld">
                  <label htmlFor="pvis-email">Email</label>
                  <input
                    id="pvis-email"
                    type="email"
                    placeholder="name@company.com"
                    value={addEmail}
                    autoComplete="off"
                    list="pvis-known"
                    onChange={(e) => setAddEmail(e.target.value)}
                  />
                  {/* Known accounts are offered as suggestions, not as the only choice. */}
                  <datalist id="pvis-known">
                    {addable.map((u) => (
                      <option key={u.email} value={u.email}>
                        {u.full_name}
                      </option>
                    ))}
                  </datalist>
                </div>
                <div className="pvis-fld">
                  <label htmlFor="pvis-role">Project role</label>
                  <select
                    id="pvis-role"
                    value={addRole}
                    onChange={(e) => setAddRole(e.target.value)}
                  >
                    <option value="">Choose a role…</option>
                    {grantable.map((k) => (
                      <option key={k} value={k}>
                        {roleLabels[k] ?? k}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="pvis-add-go"
                  disabled={!addEmail.trim() || !addRole}
                  onClick={() => {
                    addUser();
                    setAdding(false);
                  }}
                >
                  Add
                </button>
                <button type="button" className="pvis-add-x" onClick={() => setAdding(false)}>
                  Cancel
                </button>
              </div>
            ) : null}

            {grantable.length === 0 ? (
              <p className="pvis-nogrant">
                Your role does not allow granting project roles. A role can only be given to
                someone below your own.
              </p>
            ) : null}

            {/* ONLY THE ROSTER SCROLLS. The project selector above stays put, so you always
                know which project you are editing while paging through its people. */}
            <div className="pvis-scroll">
            <table className="pvis-table">
              <thead>
                <tr>
                  <th style={{ width: '26%' }}>Name</th>
                  <th style={{ width: '30%' }}>Email</th>
                  <th style={{ width: '24%' }}>Project role</th>
                  <th style={{ width: '12%' }}>Source</th>
                  <th style={{ width: '10%' }} className="center-align">
                    Access
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="grey-text center-align">
                      No team members found. Add a login user below, or define team members in
                      Stream Definition.
                    </td>
                  </tr>
                ) : (
                  people.map((p, i) => (
                    <tr key={p.email}>
                      <td>{p.name || '—'}</td>
                      <td>{p.email}</td>
                      <td>
                        {/*
                          The role this person holds ON THIS PROJECT — editable in place,
                          and offering only roles this viewer may grant. A role already set
                          that is ABOVE the viewer stays visible but cannot be re-selected,
                          because hiding it would make the row look unassigned.
                        */}
                        <select
                          className="pvis-role"
                          value={p.member_role ?? ''}
                          disabled={grantable.length === 0}
                          onChange={(e) => {
                            const v = e.target.value;
                            setPeople((cur) =>
                              cur.map((x, xi) => (xi === i ? { ...x, member_role: v } : x)),
                            );
                          }}
                        >
                          <option value="">— No role —</option>
                          {p.member_role && !grantable.includes(p.member_role) ? (
                            <option value={p.member_role} disabled>
                              {roleLabels[p.member_role] ?? p.member_role}
                            </option>
                          ) : null}
                          {grantable.map((k) => (
                            <option key={k} value={k}>
                              {roleLabels[k] ?? k}
                            </option>
                          ))}
                        </select>
                        {p.stream ? <div className="pvis-stream">{p.stream}</div> : null}
                      </td>
                      <td>
                        <span className="chip" style={{ fontSize: '.75rem' }}>
                          {p.source}
                        </span>
                      </td>
                      <td className="center-align">
                        <div className="switch">
                          <label>
                            <input
                              type="checkbox"
                              checked={p.enabled}
                              onChange={(e) => {
                                const on = e.target.checked;
                                setPeople((cur) =>
                                  cur.map((x, xi) => (xi === i ? { ...x, enabled: on } : x)),
                                );
                              }}
                            />
                            <span className="lever" />
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* The save action sits outside the scroller, so it is reachable without
                scrolling to the foot of a long roster. */}
            <div className="pvis-foot">
              <button type="button" className="btn" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save Visibility'}
              </button>
            </div>

            {toast ? <div className={`pm-toast ${toast.kind}`}>{toast.text}</div> : null}
          </div>
        </div>
      ) : null}

      {!current && toast ? <div className={`pm-toast ${toast.kind}`}>{toast.text}</div> : null}
    </div>
  );
}
